import React, { useEffect, useMemo, useState } from 'react';
import { useStoreState } from '@proscom/prostore-react';
import { FieldArray, FormikProps } from 'formik';
import { EMDASH, joinNonEmpty } from '@proscom/ui-utils';
import { isNil } from 'lodash-es';
import { startOfToday } from 'date-fns';
import { addDays } from 'date-fns';
import { FormSection } from '../../../../common/components/ui/Form/FormSection/FormSection';
import { FormSelect } from '../../../../common/components/ui/Form/FormSelect/FormSelect';
import { TextFieldPreview } from '../../../../common/components/ui/TextFieldPreview/TextFieldPreview';
import { FormDatePicker } from '../../../../common/components/ui/Form/FormDatePicker/FormDatePicker';
import { useCurrentUser } from '../../../../common/hooks/useCurrentUser';
import { VacationAppProps } from '../../../../store/vacations/VacationsUtils';
import { UserUtils } from '../../../../store/users/UserUtils';
import { SelectSize } from '../../../../common/components/ui/Select/Select';
import { PositionType, Sex } from '../../../../graphql/types';
import { convertDataListToSelectOptions } from '../../../../utils/form/formConverters';
import {
  HelpText,
  HelpTextType
} from '../../../../common/components/ui/HelpText/HelpText';
import { DatePickerProps } from '../../../../common/components/ui/DatePicker/DatePicker';
import { Entity } from '../../../../common/components/ui/AsyncSelect/entities/types';
import { FormAsyncEntitySelect } from '../../../../common/components/ui/Form/FormAsyncEntitySelect/FormAsyncEntitySelect';
import { useUser } from '../../../../graphql/hooks/useUser';
import {
  vacationReasonOptions,
  vacationTypeOptions,
  vacationChildKinshipDegreeOptions
} from '../../../../utils/form/vacationSelectOptions';
import { AbsenceTypesStoreState } from '../../../../store/AbsenceTypesStore';
import { tryNumberLike } from '../../../../utils/number';
import { FormTextArea } from '../../../../common/components/ui/Form/FormTextArea/FormTextArea';
import { FormCheckbox } from '../../../../common/components/ui/Form/FormCheckbox/FormCheckbox';
import { FormTextField } from '../../../../common/components/ui/Form/FormTextField/FormTextField';
import { FormFileLoader } from '../../../../common/components/ui/Form/FormFileLoader/FormFileLoader';
import { FileTypeEnum } from '../../../../store/FileTypeEnum';
import { Absences } from '../../../../common/components/ui/Absences/Absences';
import {
  Button,
  ButtonSize,
  ButtonVariant
} from '../../../../common/components/ui/Button';
import { getDateUtc } from '../../../../utils/date/date';
import {
  VACATION_TYPE_WITHOUT_PAY,
  VACATION_TYPE_STUDY_WITHOUT_PAY,
  VACATION_TYPE_STUDY_WITH_PAY,
  VACATION_TYPE_FOR_DANGEROUS_WORKING_CONDITIONS,
  VACATION_TYPE_PREGNANCY,
  VACATION_TYPE_CHILD_CARE,
  VACATION_TYPE_DISABLED_CHILD_CARE,
  VACATION_TYPE_BLOOD_DONATION,
  VACATION_REASON_WITHOUT_PAY_OTHER
} from './VacationAppForm';
import { VacationAppChildPeriod } from './VacationAppChildPeriod/VacationAppChildPeriod';
import s from './VacationAppForm.module.scss';

export interface VacationAppFormMainProps
  extends FormikProps<VacationAppProps> {
  minStartDate: DatePickerProps['minDate'];
  maxStartDate: DatePickerProps['maxDate'];
  minEndDate: DatePickerProps['minDate'];
  maxEndDate: DatePickerProps['maxDate'];
  excludeDates: DatePickerProps['excludeDates'];
  minStartDateHint?: string | null;
  positions: PositionType[];
  selectedPositionId: number | null;
  selectedVacationType: number | null;
  daysAvailable: number | null;
  selectedDaysAmount: number | null;
  isVacationsDisabled: boolean;
  dangerousVacationAvailable: boolean;
}

// тип отпуска в балансе отпускных дней
export const VACATION_BALANCE_TYPE_DANGEROUS =
  'За вредные и (или) опасные условия труда';

export const VacationAppFormMain: React.FC<VacationAppFormMainProps> = ({
  minStartDate,
  maxStartDate,
  minEndDate,
  maxEndDate,
  excludeDates,
  minStartDateHint,
  positions,
  selectedPositionId,
  selectedVacationType,
  daysAvailable,
  selectedDaysAmount,
  isVacationsDisabled,
  dangerousVacationAvailable,
  values,
  errors,
  setFieldValue
}) => {
  const deputyId = values.deputy_id;
  const isHeadPosition = values.isHeadPosition;
  const startDate = values.start;

  const { user: currentUser } = useCurrentUser();
  const userQuery = useUser(currentUser?.id);
  const user = userQuery.state.data;

  const { absenceTypes } = useStoreState<AbsenceTypesStoreState>(
    'STORE_ABSENCE_TYPES'
  );

  const vacationTypes = useMemo(() => {
    return absenceTypes.map((absenceType) => {
      return absenceType.type.toString();
    });
  }, [absenceTypes]);

  const deputyFilter = useMemo(() => {
    return {
      absence_dates: {
        start: values.start,
        end: values.end
      },
      absence_type: 'vacations',
      raw_absence: true,
      // передаем все возможные типы отпусков
      vacation_subtype: vacationTypes
    };
  }, [values.start, values.end, vacationTypes]);

  // можно ли создавать дополнительный период отпуска для "По уходу за детьми-инвалидами"
  const allowChildPeriodCreate = useMemo(() => {
    const baseValuesFilled = values.start && values.end;

    if (
      values.disabled_child_vacation_periods &&
      values.disabled_child_vacation_periods.length
    ) {
      // если есть период, то проверяем заполнение последнего периода и базовых дат начала-конца отпуска
      const lastPeriod =
        values.disabled_child_vacation_periods[
          values.disabled_child_vacation_periods.length - 1
        ];
      return baseValuesFilled && lastPeriod.start && lastPeriod.end;
    }
    return baseValuesFilled;
  }, [values.start, values.end, values.disabled_child_vacation_periods]);

  const minStartDateForChildPeriod = useMemo(() => {
    if (!values.end) {
      return null;
    }

    const nextDayAfterBaseEnd = addDays(new Date(getDateUtc(values.end)), 1);
    if (
      !values.disabled_child_vacation_periods ||
      !values.disabled_child_vacation_periods.length
    ) {
      return nextDayAfterBaseEnd;
    }

    const periods = values.disabled_child_vacation_periods;
    if (periods.length > 1) {
      const previousPeriod =
        values.disabled_child_vacation_periods[
          values.disabled_child_vacation_periods.length - 2
        ];
      // плюс один день от конца предыдущего периода
      return addDays(new Date(getDateUtc(previousPeriod.end)), 1);
    }
    return nextDayAfterBaseEnd;
  }, [values.end, values.disabled_child_vacation_periods]);

  const minEndDateForChildPeriod = useMemo(() => {
    if (!values.end) {
      return null;
    }

    if (
      !values.disabled_child_vacation_periods ||
      !values.disabled_child_vacation_periods.length
    ) {
      return new Date(getDateUtc(values.end));
    }

    const periods = values.disabled_child_vacation_periods;
    const currentPeriod = periods[periods.length - 1];
    if (currentPeriod.start) {
      return new Date(getDateUtc(currentPeriod.start));
    }

    return minStartDateForChildPeriod;
  }, [
    values.end,
    values.disabled_child_vacation_periods,
    minStartDateForChildPeriod
  ]);

  const vacationTypeOptionsToOutput = useMemo(
    () =>
      vacationTypeOptions.filter((vacationTypeOption) => {
        if (Number(vacationTypeOption.value) === VACATION_TYPE_PREGNANCY) {
          return user && user.sex === Sex.Female;
        } else if (
          Number(vacationTypeOption.value) ===
          VACATION_TYPE_FOR_DANGEROUS_WORKING_CONDITIONS
        ) {
          return dangerousVacationAvailable;
        } else {
          return true;
        }
      }),
    [user, dangerousVacationAvailable]
  );

  const [selectVacationDatesKey, setSelectVacationDatesKey] = useState(1);

  useEffect(() => {
    setSelectVacationDatesKey((prev) => prev + 1);
  }, [deputyFilter]);

  const deputyQuery = useUser(deputyId || '', deputyFilter);
  const deputy = deputyQuery.state.data;

  const deputyLoading = deputyQuery.check.spinner;
  const deputyPositionsSelectOptions = useMemo(() => {
    if (!deputy?.positions?.length) return [];

    return convertDataListToSelectOptions(
      deputy.positions.map((pos) => {
        const posUnit = UserUtils.getPositionUnit(pos);
        return {
          id: pos.id,
          // name: joinNonEmpty([posUnit, `${pos.c_position_name} (${pos.c_combine_position_work})`], ' - ')
          name: `${pos.c_position_name} (${joinNonEmpty(
            [posUnit, pos.c_combine_position_work],
            ', '
          )})`
        };
      }),
      {
        valueKey: 'id',
        labelKey: 'name'
      }
    );
  }, [deputy]);

  const absences = useMemo(() => {
    return deputy?.absences;
  }, [deputy]);

  const positionsSelectOptions = useMemo(() => {
    return convertDataListToSelectOptions(
      (positions || []).map((pos) => ({
        id: pos.id,
        name: `${pos.c_position_name} (${pos.c_combine_position_work})`
      })),
      {
        valueKey: 'id',
        labelKey: 'name'
      }
    );
  }, [positions]);

  const selectedPositionUnit = useMemo(() => {
    if (isNil(selectedPositionId)) return null;
    const selectedPosition = positions.find((p) => p.id === selectedPositionId);
    return UserUtils.getPositionUnit(selectedPosition);
  }, [positions, selectedPositionId]);

  const selectedDaysAmountError = errors.selectedDaysAmount;

  const selectedReason = tryNumberLike(values.no_pay_reason, null, true);

  const selectedBirthDate =
    selectedVacationType === VACATION_TYPE_CHILD_CARE
      ? !!values?.date_of_birth
      : true;
  useEffect(() => {
    if (deputyPositionsSelectOptions.length === 1) {
      setFieldValue(
        'deputy_position_id',
        deputyPositionsSelectOptions[0].value
      );
    }
  }, [deputyPositionsSelectOptions, setFieldValue]);

  useEffect(() => {
    setFieldValue('end', undefined);
  }, [startDate, setFieldValue]);

  // сбрасываем даты отпуска при смене типа отпуска, чтобы нельзя было переключиться из типа с свободным выбором днем в Плановый
  useEffect(() => {
    setFieldValue('start', undefined);
    setFieldValue('end', undefined);

    // сбрасываем массив периодов для типа отпуска "по уходу за детьми-инвалидами"
    setFieldValue('disabled_child_vacation_periods', undefined);
  }, [values.vacation_type, setFieldValue]);

  const childPeriodFieldName = 'disabled_child_vacation_periods';
  const periods = values.disabled_child_vacation_periods || [];
  const lastPeriodId = periods[periods.length - 1]?.id || 0;

  return (
    <>
      <FormSection className={s.VacationAppFormMain__positionSection}>
        <FormSelect
          name={'position_id'}
          label={'Должность'}
          placeholder={'Выберите должность'}
          options={positionsSelectOptions}
          size={SelectSize.large}
        />
      </FormSection>

      <FormSection>
        <TextFieldPreview
          className={s.VacationAppFormMain__textFieldPreview}
          classes={{
            title: s.VacationAppFormMain__textFieldPreviewTitle,
            content: s.VacationAppFormMain__textFieldPreviewContent
          }}
          title={'Подразделение:'}
        >
          {selectedPositionUnit || EMDASH}
        </TextFieldPreview>
      </FormSection>

      <FormSection className={s.VacationAppFormMain__vacationType}>
        <FormSelect
          name={'vacation_type'}
          label={'Вид отпуска'}
          placeholder={
            !selectedPositionId
              ? 'Сначала выберите должность'
              : 'Выберите вид отпуска'
          }
          options={vacationTypeOptionsToOutput}
          size={SelectSize.large}
          isDisabled={!selectedPositionId}
        />
      </FormSection>

      {Boolean(selectedPositionId && selectedVacationType) && (
        <>
          {selectedVacationType === VACATION_TYPE_CHILD_CARE && (
            <>
              <FormSection>
                <FormFileLoader
                  name={'birth_certificate'}
                  fileType={FileTypeEnum.app_document}
                  value={values?.birth_certificate}
                  label={'Свидетельство о рождении ребенка'}
                  multiple={false}
                />
                <HelpText type={HelpTextType.hint}>
                  Прикрепляется однократно при подаче заявления впервые
                </HelpText>
              </FormSection>

              <FormSection
                className={s.VacationAppFormMain__vacationOnlineDocument}
              >
                <FormDatePicker
                  name={'date_of_birth'}
                  label={'Дата рождения ребенка'}
                />
              </FormSection>
            </>
          )}
          {selectedVacationType !== VACATION_TYPE_BLOOD_DONATION && (
            <>
              <FormSection>
                <FormDatePicker
                  name={`start`}
                  label={'Дата начала отпуска'}
                  placeholder={
                    !selectedPositionId ? 'Сначала выберите должность' : ''
                  }
                  minDate={minStartDate}
                  maxDate={maxStartDate}
                  excludeDates={excludeDates}
                  disabled={!daysAvailable || !selectedBirthDate}
                />
                {minStartDateHint && (
                  <HelpText type={HelpTextType.hint}>
                    {minStartDateHint}
                  </HelpText>
                )}
              </FormSection>

              <FormSection className={s.VacationAppFormMain__endDateSection}>
                <FormDatePicker
                  name={`end`}
                  label={'Дата окончания отпуска'}
                  placeholder={
                    !selectedPositionId ? 'Сначала выберите должность' : ''
                  }
                  minDate={minEndDate}
                  maxDate={maxEndDate}
                  excludeDates={excludeDates}
                  disabled={!daysAvailable || !selectedBirthDate}
                />
              </FormSection>
            </>
          )}
          {selectedVacationType === VACATION_TYPE_DISABLED_CHILD_CARE && (
            // возможность вводить дополнительные периода отпуска
            <FieldArray name={childPeriodFieldName}>
              {({ push, remove }) => (
                <>
                  {periods.map((item, iItem) => (
                    <div key={item.id} className={s.VacationBlock__period}>
                      <VacationAppChildPeriod
                        start={item.start}
                        end={item.end}
                        index={iItem}
                        mainFieldName={childPeriodFieldName}
                        onRemovePeriod={remove}
                        minStartDate={minStartDateForChildPeriod}
                        maxStartDate={maxStartDate}
                        minEndDate={minEndDateForChildPeriod}
                        maxEndDate={maxEndDate}
                        excludeDates={excludeDates}
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  ))}

                  {allowChildPeriodCreate && (
                    <Button
                      className={s.VacationBlock__addPeriodButton}
                      variant={ButtonVariant.tertiary}
                      size={ButtonSize.small}
                      onClick={() =>
                        push({ id: lastPeriodId + 1, start: '', end: '' })
                      }
                    >
                      +
                    </Button>
                  )}
                </>
              )}
            </FieldArray>
          )}
          {selectedVacationType !== VACATION_TYPE_BLOOD_DONATION && (
            <FormSection>
              <TextFieldPreview
                className={s.VacationAppFormMain__textFieldPreview}
                classes={{
                  title: s.VacationAppFormMain__textFieldPreviewTitle,
                  content: s.VacationAppFormMain__textFieldPreviewContent
                }}
                title={'Количество календарных дней отпуска:'}
              >
                {selectedDaysAmount ?? EMDASH}
              </TextFieldPreview>
              <HelpText
                className={s.VacationAppFormMain__errorText}
                type={HelpTextType.error}
              >
                {selectedDaysAmountError && (
                  <>
                    Ваше заявление не может быть отправлено.
                    <br />
                    {selectedDaysAmountError}
                  </>
                )}
              </HelpText>
            </FormSection>
          )}
          {selectedVacationType === VACATION_TYPE_WITHOUT_PAY && (
            <>
              <FormSection className={s.VacationAppFormMain__vacationReason}>
                <FormSelect
                  name={'no_pay_reason'}
                  label={'Укажите причину'}
                  placeholder={
                    !selectedPositionId ? 'Сначала выберите должность' : ''
                  }
                  options={vacationReasonOptions}
                  size={SelectSize.large}
                  isDisabled={!selectedPositionId}
                />
              </FormSection>
              {selectedReason === VACATION_REASON_WITHOUT_PAY_OTHER && (
                <FormSection
                  className={s.VacationAppFormMain__vacationReasonText}
                >
                  <FormTextArea
                    name={'no_pay_reason_text'}
                    placeholder={'Укажите причину'}
                    rows={2}
                  />
                </FormSection>
              )}
            </>
          )}
          {[
            VACATION_TYPE_STUDY_WITHOUT_PAY,
            VACATION_TYPE_STUDY_WITH_PAY
          ].includes(selectedVacationType ?? 0) && (
            <FormSection>
              <FormFileLoader
                name={'invitation_note'}
                fileType={FileTypeEnum.app_document}
                label={'Справка-вызов'}
                value={values?.invitation_note}
                multiple={false}
              />
            </FormSection>
          )}
          {selectedVacationType === VACATION_TYPE_PREGNANCY && (
            <>
              <FormSection
                className={s.VacationAppFormMain__vacationOnlineDocument}
              >
                <FormCheckbox
                  name={'digital_sick_leave_issued'}
                  label={'Выдан электронный больничный'}
                />
              </FormSection>

              <FormSection
                className={s.VacationAppFormMain__vacationOnlineDocumentNumber}
              >
                <FormTextField
                  name={'sick_leave_number'}
                  label={'Номер электронного больничного'}
                  disabled={!values.digital_sick_leave_issued}
                />
              </FormSection>

              {!values.digital_sick_leave_issued && (
                <FormSection>
                  <FormFileLoader
                    name={'sick_leave_doc'}
                    fileType={FileTypeEnum.app_document}
                    label={'Больничный'}
                    value={values?.sick_leave_doc}
                    multiple={false}
                  />
                </FormSection>
              )}
            </>
          )}
          {selectedVacationType === VACATION_TYPE_DISABLED_CHILD_CARE && (
            <>
              <FormSection>
                Данные ребенка
                <FormSection>
                  <FormTextField
                    name={'child_surname'}
                    label={'Фамилия'}
                    placeholder="Фамилия"
                  />
                </FormSection>
                <FormSection>
                  <FormTextField
                    name={'child_name'}
                    label={'Имя'}
                    placeholder="Имя"
                  />
                </FormSection>
                <FormSection>
                  <FormTextField
                    name={'child_patronymic'}
                    label={'Отчество'}
                    placeholder="Отчество"
                  />
                </FormSection>
                <FormSection>
                  <FormSection>
                    <FormDatePicker
                      name={`child_birth_date`}
                      label={'Дата рождения'}
                      placeholder="Дата рождения"
                    />
                  </FormSection>
                  <FormSection>
                    <FormTextField
                      name={'child_snils'}
                      label={'СНИЛС'}
                      placeholder="СНИЛС"
                      maskProps={{ mask: '999-999-999 99' }}
                    />
                  </FormSection>
                  <FormSection>
                    <FormSelect
                      name={'child_kinship_degree'}
                      label={'Степень родства *'}
                      options={vacationChildKinshipDegreeOptions}
                      size={SelectSize.large}
                    />
                  </FormSection>
                </FormSection>
              </FormSection>
              <FormSection>
                <FormFileLoader
                  name={'birth_certificate'}
                  fileType={FileTypeEnum.app_document}
                  value={values?.birth_certificate}
                  label={'Свидетельство о рождении ребенка'}
                  multiple={false}
                />
                <HelpText type={HelpTextType.hint}>
                  Прикрепляется однократно при подаче заявления впервые
                </HelpText>
              </FormSection>
              <FormSection>
                <FormFileLoader
                  name={'disability_certificate'}
                  fileType={FileTypeEnum.app_document}
                  value={values?.disability_certificate}
                  label={'Справка об инвалидности'}
                  multiple={false}
                />
              </FormSection>
              <FormSection>
                <FormCheckbox
                  name={'disability_permanent'}
                  label={'Бессрочная инвалидность'}
                />
              </FormSection>
              {!values.disability_permanent && (
                <>
                  <FormSection>
                    <FormDatePicker
                      name={`disability_start`}
                      label={'Срок установления инвалидности'}
                      placeholder={'Дата начала'}
                    />
                  </FormSection>

                  <FormSection
                    className={s.VacationAppFormMain__endDateSection}
                  >
                    <FormDatePicker
                      name={`disability_end`}
                      placeholder={'Дата окончания'}
                    />
                  </FormSection>
                </>
              )}
              <FormSection>
                <FormFileLoader
                  name={'confirm_child_place'}
                  fileType={FileTypeEnum.app_document}
                  value={values?.confirm_child_place}
                  label={'Документ, подтверждающий место жительства ребенка'}
                  multiple={false}
                />
                <HelpText type={HelpTextType.hint}>
                  Если место жительства (пребывания или фактического проживания)
                  ребенка изменилось, прикрепите подтверждающий документ
                </HelpText>
              </FormSection>
              <FormSection>
                <FormFileLoader
                  name={'cert_other_parent_place'}
                  fileType={FileTypeEnum.app_document}
                  value={values?.cert_other_parent_place}
                  label={'Справка с места работы другого родителя *'}
                  multiple={false}
                />
                <HelpText type={HelpTextType.hint}>
                  Справка о том, что дополнительные оплачиваемые выходные дни не
                  использованы или документ, подтверждающий факт смерти другого
                  родителя
                </HelpText>
              </FormSection>
              <FormSection>
                <FormFileLoader
                  name={'cert_app_prev_place'}
                  fileType={FileTypeEnum.app_document}
                  value={values?.cert_app_prev_place}
                  label={'Справка с предыдущего места работы заявителя *'}
                  multiple={false}
                />
                <HelpText type={HelpTextType.hint}>
                  Справка о том, что на дату увольнения доп.выходные дни не
                  использованы или использованы частично с указанием дат и
                  общего количества использованных дней
                </HelpText>
              </FormSection>
              <FormSection>
                <FormFileLoader
                  name={'cert_other_parent_prev_place'}
                  fileType={FileTypeEnum.app_document}
                  value={values?.cert_other_parent_prev_place}
                  label={
                    'Справка с предыдущего места работы другого родителя *'
                  }
                  multiple={false}
                />
                <HelpText type={HelpTextType.hint}>
                  Справка о том, что на дату увольнения доп.выходные дни не
                  использованы или использованы частично с указанием дат и
                  общего количества использованных дней
                </HelpText>
              </FormSection>
            </>
          )}
          {selectedVacationType === VACATION_TYPE_BLOOD_DONATION && (
            <>
              <FormSection>
                <FormDatePicker
                  name={`blood_donation_date`}
                  label={'Дата сдачи крови'}
                  placeholder={''}
                />
              </FormSection>
              <FormSection>
                <FormCheckbox
                  name={'absent_from_work'}
                  label={'Отсутствовал на работе в день сдачи крови?'}
                />
              </FormSection>

              <FormSection>
                <FormDatePicker
                  name={`first_day_date`}
                  label={'Дата первого выходного дня'}
                  minDate={
                    values.blood_donation_date
                      ? new Date(values.blood_donation_date)
                      : startOfToday()
                  }
                  placeholder={''}
                />
              </FormSection>

              {!values.absent_from_work && (
                <FormSection>
                  <FormDatePicker
                    name={`second_day_date`}
                    label={'Дата второго выходного дня'}
                    placeholder={''}
                    minDate={
                      values.blood_donation_date
                        ? new Date(values.blood_donation_date)
                        : startOfToday()
                    }
                    excludeDates={
                      values.first_day_date
                        ? [new Date(values.first_day_date)]
                        : []
                    }
                  />
                </FormSection>
              )}

              <FormSection>
                <FormFileLoader
                  name={'reference'}
                  fileType={FileTypeEnum.app_document}
                  value={values?.reference}
                  label={'Справка'}
                  multiple={false}
                />
              </FormSection>

              <FormSection>
                <FormTextField
                  name={'reference_number'}
                  label={'Номер справки и от какого числа'}
                />
              </FormSection>
            </>
          )}
        </>
      )}

      {isHeadPosition && selectedVacationType && (
        <>
          <FormSection>
            <FormAsyncEntitySelect
              key={selectVacationDatesKey}
              name={'deputy_id'}
              label={
                'Сотрудник, который будет исполнять обязанности руководителя на период отпуска'
              }
              placeholder={'Начните вводить ФИО сотрудника'}
              size={SelectSize.large}
              entity={Entity.usersPage}
              filter={deputyFilter}
              disabled={isVacationsDisabled}
              isCreatable={false}
              rawLabel={true}
            />

            <Absences absences={absences} />
          </FormSection>
          <FormSection>
            <FormSelect
              name={'deputy_position_id'}
              label={'Должность заместителя'}
              placeholder={'Выберите должность заместителя'}
              options={deputyPositionsSelectOptions}
              size={SelectSize.large}
              isLoading={deputyLoading}
              isDisabled={
                deputyPositionsSelectOptions.length <= 1 || isVacationsDisabled
              }
            />
          </FormSection>
        </>
      )}
    </>
  );
};
